import { request } from "../api.js";

/**
 * The applicant portal's API client, on top of the same `request` helper the form app uses — one
 * cookie policy, one error shape, no second copy to keep in step.
 */

export interface PortalApplication {
  id: string;
  reference: string | null;
  formTitle: string;
  cycle: string | null;
  submittedAt: string | null;
  status: string;
  canEdit: boolean;
}

export interface PortalProfileValue {
  key: string;
  label: string;
  category: string;
  value: unknown;
  sourceCycle: string | null;
  updatedAt: string;
}

export interface PortalProfile {
  respondent: { email: string; displayName: string | null };
  profile: PortalProfileValue[];
}

export interface PortalApplicationDetail {
  reference: string | null;
  formTitle: string;
  cycle: string | null;
  submittedAt: string | null;
  formVersion: number;
  answers: { key: string; label: string; value: unknown; isSensitive: boolean }[];
}

export const fetchApplications = (): Promise<{ data: PortalApplication[] }> =>
  request("/portal/applications");

export const fetchApplication = (id: string): Promise<PortalApplicationDetail> =>
  request(`/portal/applications/${encodeURIComponent(id)}`);

/** An applicant reading their own encrypted answers back. Audited, but never gated by a reason. */
export const revealOwnAnswers = (
  submissionId: string,
  fieldKeys: string[],
): Promise<{ values: Record<string, string>; unanswered: string[] }> =>
  request(`/portal/applications/${encodeURIComponent(submissionId)}/reveal`, {
    method: "POST",
    body: JSON.stringify({ fieldKeys }),
  });

export const fetchProfile = (): Promise<PortalProfile> => request("/portal/me");

export const saveProfile = (values: Record<string, unknown>): Promise<{ changed: number }> =>
  request("/portal/me", { method: "PATCH", body: JSON.stringify({ values }) });

export const requestErasure = (
  note: string,
): Promise<{ received: boolean; type: string; message: string }> =>
  request("/portal/data-request", {
    method: "POST",
    body: JSON.stringify({ type: "erasure", note }),
  });

/**
 * The data download is a file, not JSON to render — it goes through the browser's own download
 * path rather than `request`, so the Content-Disposition filename the API sets is honoured.
 */
export const downloadMyData = (): void => {
  window.location.href = "/api/v1/portal/me/data";
};
